
import React, { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Download, Filter, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface KpiDetailLayoutProps {
  id: string;
  title: string;
  icon?: React.ReactNode;
  colorClass?: string;
  children?: ReactNode; // Make children optional
  tabs?: { id: string; label: string; content: ReactNode }[];
}

const KpiDetailLayout: React.FC<KpiDetailLayoutProps> = ({
  id,
  title,
  icon,
  colorClass = "bg-blue-50 dark:bg-blue-900/20",
  children,
  tabs
}) => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/energy/top-kpis');
  };

  const handleExport = () => {
    // Handle export functionality (would be implemented in a real app)
    console.log('Exporting data for', title);
  };

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-[calc(100vh-4rem)]">
      {/* Breadcrumb navigation */}
      <div className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        <Link to="/energy/dashboard" className="hover:text-blue-600 dark:hover:text-blue-400">Dashboard</Link>
        <span className="mx-2">/</span>
        <Link to="/energy/top-kpis" className="hover:text-blue-600 dark:hover:text-blue-400">Top KPIs</Link>
        <span className="mx-2">/</span>
        <span className="text-gray-900 dark:text-gray-100">{title}</span>
      </div>

      {/* Header with actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            size="icon" 
            className="h-10 w-10" 
            onClick={handleBack}
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center">
            {icon && (
              <div className={cn("p-2 rounded-lg mr-3", colorClass)}>
                {icon}
              </div>
            )}
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{title}</h1>
          </div>
        </div>
        
        <div className="flex gap-2 self-end sm:self-auto">
          <Button variant="outline" size="sm" className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            Last 30 Days
          </Button>
          <Button variant="outline" size="sm" className="flex items-center gap-1">
            <Filter className="h-4 w-4" />
            Filter
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            className="flex items-center gap-1"
            onClick={handleExport}
          >
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Main content */}
      {tabs ? (
        <Tabs defaultValue={tabs[0].id} className="w-full">
          <TabsList className="mb-4">
            {tabs.map(tab => (
              <TabsTrigger key={tab.id} value={tab.id}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {tabs.map(tab => (
            <TabsContent key={tab.id} value={tab.id} className="mt-0">
              <Card className="border-none shadow-sm">
                {tab.content}
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        <Card className="border-none shadow-sm">
          {children}
        </Card>
      )}
    </div>
  );
};

export default KpiDetailLayout;
